import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useOrganization } from '@/contexts/OrganizationContext';

/** ENT-6091: resolve email CTA organisation switches before any advisory listing read. */
export function useAdvisoryOrganization(mspView = false) {
  const { organization, user, isLoading, isMspContext, mspHomeOrgId, mspHomeOrg, switchOrganization } = useOrganization();
  const [params] = useSearchParams();
  const target = params.get('org');
  const [attempt, setAttempt] = useState(0);
  const [failure, setFailure] = useState<{ key: string; kind: 'forbidden' | 'unavailable' } | null>(null);
  const attempted = useRef<{ key: string; switchOrganization: typeof switchOrganization; promise: Promise<void> } | null>(null);
  const key = `${user?.id ?? ''}:${target ?? ''}`;
  const error = target && organization?.id !== target && failure?.key === key ? failure.kind : null;
  useEffect(() => {
    if (isLoading || !user?.id || !target || organization?.id === target) return;
    const attemptKey = `${key}:${organization?.id ?? ''}:${attempt}`;
    // The initial bootstrap exposes the active org before its complete workspace
    // choices hydrate. A new switch callback must not reuse that earlier refusal.
    if (attempted.current?.key !== attemptKey || attempted.current.switchOrganization !== switchOrganization) {
      setFailure(null);
      attempted.current = { key: attemptKey, switchOrganization, promise: switchOrganization(target) };
    }
    let active = true;
    void attempted.current.promise.catch((cause: unknown) => {
      if (!active) return;
      const code = cause instanceof Error ? cause.message : '';
      setFailure({ key, kind: /forbidden|access_denied|not_authorized|unknown_organization_context_target/.test(code) ? 'forbidden' : 'unavailable' });
    });
    return () => { active = false; };
  }, [mspView, isLoading, user?.id, target, organization?.id, switchOrganization, key, attempt]);
  const switching = Boolean(user?.id && target && organization?.id !== target);
  return {
    contextId: switching ? null : (mspView && isMspContext ? mspHomeOrgId : organization?.id) ?? null,
    userId: user?.id ?? null,
    companyName: mspView && isMspContext ? mspHomeOrg?.name ?? 'your managed customers' : organization?.name ?? 'this company',
    loading: isLoading || (switching && !error),
    error,
    retry: () => { setFailure(null); setAttempt((value) => value + 1); },
  };
}
